(() => {
  const initializeViewer = (inlineViewer) => {
    if (inlineViewer.dataset.product3dInitialized === 'true') return;

    const mediaWrapper = inlineViewer.closest('.product__media-wrapper');
    const gallery = Array.from(mediaWrapper?.children || []).find((element) => element.matches('media-gallery'));
    const controls = mediaWrapper?.querySelector('[data-product-3d-controls]');
    const openButton = controls?.querySelector('[data-product-3d-open]');
    const arButton = controls?.querySelector('[data-product-3d-ar]');

    if (!mediaWrapper || !gallery || !controls || !openButton || !arButton) return;

    const label360 = openButton.querySelector('[data-product-3d-label-360]');
    const labelFlat = openButton.querySelector('[data-product-3d-label-flat]');
    const stage = inlineViewer.querySelector('[data-product-3d-stage]');
    const viewer = inlineViewer.querySelector('[data-product-3d-model]');
    const loader = inlineViewer.querySelector('[data-product-3d-loader]');
    const progress = inlineViewer.querySelector('[data-product-3d-progress]');
    const percentage = inlineViewer.querySelector('[data-product-3d-percentage]');
    const status = inlineViewer.querySelector('[data-product-3d-status]');
    let modelLoaded = false;

    const getImageSurface = () =>
      gallery.querySelector('.product__media-item.is-active .product__media') ||
      gallery.querySelector('.product__media');
    const initialImageSurface = getImageSurface();

    if (!initialImageSurface || !stage || !viewer || !loader || !progress || !percentage || !status) return;

    const syncViewerSize = () => {
      const imageSurface = getImageSurface();
      const imageHeight = imageSurface?.getBoundingClientRect().height;
      if (imageHeight > 0) inlineViewer.style.setProperty('--product-3d-media-height', `${imageHeight}px`);
    };

    inlineViewer.dataset.product3dInitialized = 'true';
    syncViewerSize();
    initialImageSurface.appendChild(controls);

    if ('ResizeObserver' in window) {
      const imageResizeObserver = new ResizeObserver(syncViewerSize);
      imageResizeObserver.observe(initialImageSurface);
      inlineViewer.product3dResizeObserver = imageResizeObserver;
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!viewer.getAttribute('src')) viewer.setAttribute('src', viewer.dataset.modelSrc);
      });
    });

    openButton.addEventListener('click', () => {
      const showModel = openButton.getAttribute('aria-pressed') !== 'true';
      openButton.setAttribute('aria-pressed', String(showModel));
      openButton.setAttribute('aria-label', showModel ? 'View flat product images' : 'View product in 360 degrees');
      label360.hidden = showModel;
      labelFlat.hidden = !showModel;

      if (showModel) {
        syncViewerSize();
        inlineViewer.hidden = false;
        stage.appendChild(controls);
        gallery.hidden = true;
      } else {
        gallery.hidden = false;
        const imageSurface = getImageSurface();
        if (imageSurface) imageSurface.appendChild(controls);
        inlineViewer.hidden = true;
      }

      if (!showModel) return;

      status.textContent = '';
      loader.hidden = modelLoaded;
      stage.setAttribute('aria-busy', String(!modelLoaded));

      if (!viewer.getAttribute('src')) {
        progress.style.width = '0%';
        percentage.textContent = '0%';
        requestAnimationFrame(() => {
          requestAnimationFrame(() => viewer.setAttribute('src', viewer.dataset.modelSrc));
        });
      }
    });

    viewer.addEventListener('progress', (event) => {
      const loadedPercentage = Math.round(event.detail.totalProgress * 100);
      progress.style.width = `${loadedPercentage}%`;
      percentage.textContent = `${loadedPercentage}%`;
    });

    /**
     * Recolour the carcass once the model is ready.
     *
     * The GLB's "White Wood" material has a warm baked texture (average
     * #F8F4F1), which reads as cream next to the neutral product photography.
     * The hex in data-carcass-color on the <model-viewer> replaces that
     * material's base colour at runtime, so the tint can be adjusted from the
     * template without re-exporting the model. The texture is cleared first,
     * because a base-colour factor is multiplied by the texture rather than
     * replacing it, and the warm tint would otherwise survive.
     */
    const applyCarcassColor = () => {
      const hex = viewer.dataset.carcassColor;
      if (!hex) return;

      const channels = hex.replace('#', '').match(/.{2}/g);
      if (!channels || channels.length < 3) return;

      // setBaseColorFactor feeds the array straight into a three.js Color via
      // fromArray, which expects plain 0-1 sRGB values — so no linear
      // conversion here, or the colour comes out washed out.
      const [r, g, b] = channels.map((value) => parseInt(value, 16) / 255);

      const materials = viewer.model?.materials || [];
      console.log('[KG 360] applyCarcassColor running. hex:', hex, 'materials:', materials.length);

      materials.forEach((material) => {
        const pbr = material.pbrMetallicRoughness;
        const base = pbr.baseColorFactor;
        console.log('[KG 360]  -', material.name, 'baseColorFactor:', base, 'hasTexture:', !!pbr.baseColorTexture);

        // The legs are the dark material; everything lighter is carcass and
        // gets retinted. Testing the material's own brightness is more robust
        // than matching its name, which varies between exports.
        const isDark = Array.isArray(base) && base[0] < 0.2 && base[1] < 0.2 && base[2] < 0.2;
        if (isDark) return;

        try {
          pbr.baseColorTexture?.setTexture(null);
        } catch (error) {
          console.log('[KG 360]    setTexture(null) failed:', error.message);
        }
        pbr.setBaseColorFactor([r, g, b, 1]);
        console.log('[KG 360]    -> recoloured to', hex);
      });
    };

    viewer.addEventListener('load', () => {
      modelLoaded = true;
      arButton.disabled = false;
      loader.hidden = true;
      stage.setAttribute('aria-busy', 'false');
      applyCarcassColor();
    });

    viewer.addEventListener('error', () => {
      loader.hidden = true;
      arButton.disabled = true;
      stage.setAttribute('aria-busy', 'false');
      viewer.removeAttribute('src');
      status.textContent = 'The 3D model could not be loaded. Please try again.';
    });

    arButton.addEventListener('click', () => {
      status.textContent = '';

      if (!modelLoaded || !viewer.canActivateAR) {
        status.textContent = 'AR is not available on this device or browser.';
        return;
      }

      viewer.activateAR().catch(() => {
        status.textContent = 'AR could not be opened on this device or browser.';
      });
    });

    viewer.addEventListener('ar-status', (event) => {
      if (event.detail.status === 'failed') {
        status.textContent = 'AR is not available on this device or browser.';
      } else if (event.detail.status === 'session-started') {
        status.textContent = '';
      }
    });
  };

  const initializeAllViewers = (root = document) => {
    root.querySelectorAll('[data-product-3d-inline]').forEach(initializeViewer);
  };

  initializeAllViewers();
  document.addEventListener('shopify:section:load', (event) => initializeAllViewers(event.target));
})();
